<template>
  <div>
    <DataTable
      ref="DataTable"
      :data-model="'products'"
      :display-name-plural="$t('entities.Product.Plural')"
      :display-name-singular="$t('entities.Product.Singular')"
      :permission-name="'Products'"
      :allow-delete="false"
      :allow-create="false"
      :exclude-fields="['id', 'isInvalid', 'isInProgress']"
      @input="onDataTableInput">
      <template #cell(status)="row">
        {{ $t('products.Status.'+row.item.status) }}
      </template>
      <template #cell(color)="row">
        <div :style="'min-height:15px;background-color:'+(colors.filter(x => x.id == row.item.color)[0].value)"
             v-if="row.item.color !== 0" />
      </template>
      <template #cell(approvedByLaboratory)="row">
        <b-form-checkbox v-model="row.item.approvedByLaboratory"
                         @change="productToApproveItem = row.item;"
                         :disabled="!isApproveByLaboratoryAuthorized" />
      </template>
      <template #cell(pairedPhenotypesCount)="row">
        <div :class="row.item.isInvalid ? 'invalid-product-count': ''">
          {{ row.item.pairedPhenotypesCount }}
        </div>
      </template>
      <template #extraTableActions>
        <b-button
          class="ml-2"
          variant="outline-info"
          @click="$refs.reanalyseModal.show()"
          v-if="$auth.check(
            'Permissions.AdminPanel.Products.Edit'
          )
            && reanalysisInProgress === false"
          :disabled="reanalysisInProgress">
          <font-awesome-icon class="fa-md"
                             icon="diagnoses" />
          {{ $t('common.Reanalyse') }}
        </b-button>
        <v-alert
          color="#17A2B8"
          v-if="reanalysisInProgress">
          <font-awesome-icon class="fa-md"
                             icon="diagnoses" />
          {{ $t('common.ReanalysisInProgress') }}
        </v-alert>
      </template>
      <template #extraActions="row">
        <el-tooltip
          :content="$t('common.DownloadPDF')"
          :enterable="false"
          placement="top">
          <b-button class="downloadBtn mx-1"
                    @click="download(row.item)">
            <font-awesome-icon class="fa-xs"
                               icon="download" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.DownloadDoctorsIntroPDF')"
          :enterable="false"
          placement="top">
          <b-button class="downloadBtnDoctor mx-1"
                    @click="downloadDoctorsIntro(row.item)">
            <font-awesome-icon class="fa-xs"
                               icon="file-download" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.AssignDoctorToProduct')"
          :enterable="false"
          placement="top"
          v-if="isMainDoctor">
          <b-button class="assignBtnDoctor mx-1"
                    @click="productToAssign = row.item.id; $refs.assignModal.show();">
            <font-awesome-icon class="fa-md"
                               icon="briefcase-medical" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.DownloadProcessProductReport')"
          :enterable="false"
          placement="top">
          <b-button class="downloadBtnProcessProductReport mx-1"
                    @click="downloadProcessProductReport(row.item)">
            <font-awesome-icon class="fa-md"
                               icon="tasks" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.MoveToDoctorSection')"
          :enterable="false"
          placement="top">
          <b-button class="moveProductToDoctorSectionBtn mx-1"
                    @click="toMoveToDoctorSection = row.item; $refs.moveToDoctorSectionModal.show();"
                    :disabled="actionDisabled(row)">
            <font-awesome-icon class="fa-md"
                               icon="stethoscope" />
          </b-button>
        </el-tooltip>
      </template>
    </DataTable>
    <b-modal id="modal-center"
             ref="downloadModal"
             centered
             :ok-disabled="true"
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.Downloading') }}
          </h3>
        </div>
      </template>
      <p class="my-4 text-center">
        {{ $t(downloadModalMessageKey) }}
      </p>
      <div id="nprogressDownload"
           style="height:25px" />
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="danger"
          @click="cancelDownload(); $refs.downloadModal.hide()">
          {{ $t('common.Cancel') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="modal-center"
             ref="assignModal"
             centered
             :ok-disabled="true"
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.AssignDoctorToProduct') }}
          </h3>
        </div>
      </template>
      <DoctorInput v-model="doctorToAssign" />
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="danger"
          @click="doctorToAssign = 0; productToAssign = 0; $refs.assignModal.hide()">
          {{ $t('common.Cancel') }}
        </b-button>
        <b-button
          variant="success"
          @click="assignDoctorToProduct(); $refs.assignModal.hide()">
          {{ $t('common.Edit') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="modal-center"
             ref="moveToDoctorSectionModal"
             centered
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.MoveToDoctorSection') }}
          </h3>
        </div>
      </template>
      <p class="my-4 text-center">
        {{ $t('common.RealyMoveToDoctorSectionQuestion') }}
      </p>
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="secondary"
          @click="toMoveToDoctorSection = null; $refs.moveToDoctorSectionModal.hide();">
          {{ $t('common.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          @click="moveProductToDoctorSection(toMoveToDoctorSection);">
          {{ $t('common.Yes') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="modal-center"
             ref="reanalyseModal"
             centered
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.ReanalyseResults') }}
          </h3>
        </div>
      </template>
      <p class="my-4 text-center">
        {{ $t('common.RealyReanalyseResultsQuestion') }}
      </p>
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="secondary"
          @click="$refs.reanalyseModal.hide();">
          {{ $t('common.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          @click="reanalyseResults();">
          {{ $t('common.Reanalyse') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import DataTable from '@components/admin/DataTable';
import NProgress from 'nprogress/nprogress';
import DoctorColors from '@utils/doctorColors';
import { downloadBlob } from '@utils/downloadBlob';
import DoctorInput from '@components/input/DoctorInput';
import { updatePairedPhenotypesCountMixin } from '@components/mixins/updatePairedPhenotypesCount';

export default {
  components: {
    DataTable,
    DoctorInput
  },
  mixins: [
    updatePairedPhenotypesCountMixin
  ],
  data() {
    return {
      source: null,
      colors: DoctorColors,
      doctorToAssign: 0,
      productToAssign: 0,
      productToApproveItem: null,
      isMainDoctor: false,
      downloadModalMessageKey: 'common.GeneratingPDF',
      toMoveToArchive: null,
      reanalysisInProgress: null,
      refreshInterval: null
    };
  },
  computed: {
    approvedByLaboratory() {
      return this.productToApproveItem?.approvedByLaboratory;
    },
    isApproveByLaboratoryAuthorized() {
      return this.isAdmin || this.isLaborant;
    },
    role() {
      return this.$store.state['vue-auth']?.user?.role || '';
    },
    isLaborant() {
      return this.role === 'Laboratory Scientist';
    },
    isAdmin() {
      return this.role === 'Administrator';
    }
  },
  mounted() {
    this.source = this.axios.CancelToken.source();
    this.axios.get('/admin/auth/ismaindoctor')
      .then((res) => {
        this.isMainDoctor = res.data.result;
      });
    this.getProductReanalyseStatus();
  },
  beforeDestroy() {
    clearInterval(this.refreshInterval);
    this.refreshInterval = null;
  },
  watch: {
    approvedByLaboratory(newValue, oldValue) {
      if (oldValue !== undefined) {
        this.approveProduct();
      }
    },
    reanalysisInProgress(newValue, oldValue) {
      if (newValue === true && this.refreshInterval === null) {
        this.refreshInterval = setInterval(() => {
          this.$refs.DataTable.refresh();
          this.getProductReanalyseStatus();
        }, 5000);
      } else {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    }
  },
  methods: {
    onButtonClick(row, callback) {
      callback(row.item);
    },
    actionDisabled(row) {
      return row.item.isInProgress;
    },
    cancelDownload() {
      this.source.cancel();
      this.source = this.axios.CancelToken.source();
    },
    download(item) {
      this.showDownloadModal('common.GeneratingPDF');
      NProgress.configure({ parent: '#nprogressDownload' });
      NProgress.start();
      this.axios.get('admin/products/getpdf', {
        params: {
          ProductId: item.id
        },
        responseType: 'arraybuffer',
        cancelToken: this.source.token
      }).then((response) => {
        const today = new Date();
        downloadBlob(
          response.data,
          `GenScan_Vysledek_${today.toLocaleDateString('en-US')} ${item.customer}.pdf`,
          'application/pdf'
        );
        this.hideDownloadModal();
      })
        .catch((thrown) => {
          if (this.axios.isCancel(thrown)) {
            this.$root.$bvToast.toast(this.$t('common.DownloadCancel'), {
              title: this.$t('common.Cancelled'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
            this.hideDownloadModal();
          }
        })
        .finally(() => {
          NProgress.configure({ parent: 'body' });
          NProgress.done();
        });
    },
    downloadDoctorsIntro(item) {
      this.showDownloadModal('common.GeneratingPDF');
      NProgress.configure({ parent: '#nprogressDownload' });
      NProgress.start();
      this.axios.get('admin/products/getdoctorsintropdf', {
        params: {
          ProductId: item.id
        },
        responseType: 'arraybuffer',
        cancelToken: this.source.token
      }).then((response) => {
        const today = new Date();
        downloadBlob(
          response.data,
          `GenScan_Lekarska_Zprava_${today.toLocaleDateString('en-US')} ${item.customer}.pdf`,
          'application/pdf'
        );
        this.hideDownloadModal();
      })
        .catch((thrown) => {
          if (this.axios.isCancel(thrown)) {
            this.$root.$bvToast.toast(this.$t('common.DownloadCancel'), {
              title: this.$t('common.Cancelled'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
            this.hideDownloadModal();
          }
        })
        .finally(() => {
          NProgress.configure({ parent: 'body' });
          NProgress.done();
        });
    },
    downloadProcessProductReport(item) {
      this.showDownloadModal('common.GeneratingProcessProductReport');
      NProgress.configure({ parent: '#nprogressDownload' });
      NProgress.start();
      this.axios.get('admin/products/getprocessproductreport', {
        params: {
          ProductId: item.id
        },
        responseType: 'arraybuffer',
        cancelToken: this.source.token
      }).then((response) => {
        const today = new Date();
        downloadBlob(
          response.data,
          `GenScan_Report_Vytvoreni_Vysledku_${today.toLocaleDateString('en-US')}_${item.customer}.txt`,
          'application/text'
        );
        this.hideDownloadModal();
      })
        .catch((thrown) => {
          if (this.axios.isCancel(thrown)) {
            this.$root.$bvToast.toast(this.$t('common.DownloadCancel'), {
              title: this.$t('common.Cancelled'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
            this.hideDownloadModal();
          }
        })
        .finally(() => {
          NProgress.configure({ parent: 'body' });
          NProgress.done();
        });
    },
    assignDoctorToProduct() {
      this.axios.patch('admin/products/assigndoctor', {
        DoctorId: this.doctorToAssign,
        ProductId: this.productToAssign
      }).then(() => {
        this.$root.$bvToast.toast(this.$t('ViewReservations.SuccessToast.Body'), {
          title: this.$t('ViewReservations.SuccessToast.Title'),
          variant: 'success',
          toaster: 'b-toaster-top-right'
        });
        this.doctorToAssign = 0;
        this.productToAssign = 0;
        this.$refs.DataTable.refresh();
      }).catch(() => {
        this.$root.$bvToast.toast(this.$t('ViewReservations.FailToast.Body'), {
          title: this.$t('ViewReservations.FailToast.Title'),
          variant: 'danger',
          toaster: 'b-toaster-top-right'
        });
      });
    },
    approveProduct() {
      this.axios.patch('admin/products/approveByLaboratory', {
        ProductId: this.productToApproveItem.id,
        Approve: this.productToApproveItem.approvedByLaboratory
      }).then(() => {
        this.$root.$bvToast.toast(this.$t('ViewReservations.SuccessToast.Body'), {
          title: this.$t('ViewReservations.SuccessToast.Title'),
          variant: 'success',
          toaster: 'b-toaster-top-right'
        });
        this.productToApprove = null;
        this.$refs.DataTable.refresh();
      }).catch(() => {
        this.$root.$bvToast.toast(this.$t('ViewReservations.FailToast.Body'), {
          title: this.$t('ViewReservations.FailToast.Title'),
          variant: 'danger',
          toaster: 'b-toaster-top-right'
        });
      });
    },
    moveProductToDoctorSection(item) {
      this.axios.post('admin/products/moveToDoctorSection', {
        ProductId: item.id
      }).then(() => {
        this.$root.$bvToast.toast(this.$t('DoctorSection.MoveTo.SuccessToast.Body'), {
          title: this.$t('DoctorSection.MoveTo.SuccessToast.Title'),
          variant: 'success',
          toaster: 'b-toaster-top-right'
        });
        this.$refs.DataTable.removeRowById(item.id);
      }).catch(() => {
        this.$root.$bvToast.toast(this.$t('DoctorSection.MoveTo.FailToast.Body'), {
          title: this.$t('DoctorSection.MoveTo.FailToast.Title'),
          variant: 'danger',
          toaster: 'b-toaster-top-right'
        });
      }).finally(() => {
        this.toMoveToDoctorSection = null;
        this.$refs.moveToDoctorSectionModal.hide();
        NProgress.configure({ parent: 'body' });
        NProgress.done();
      });
    },
    reanalyseResults() {
      this.axios.post('admin/products/reanalyseAll', {}).then((response) => {
        this.$root.$bvToast.toast(this.$t('ProductResultsReanalysisAll.SuccessToast.Body'), {
          title: this.$t('ProductResultsReanalysisAll.SuccessToast.Title'),
          variant: 'success',
          toaster: 'b-toaster-top-right'
        });
        if (response.status === 200) {
          this.getProductReanalyseStatus();
          this.$refs.DataTable.reload();
        }
      }).catch(() => {
        this.$root.$bvToast.toast(this.$t('ProductResultsReanalysisAll.FailToast.Body'), {
          title: this.$t('ProductResultsReanalysisAll.FailToast.Title'),
          variant: 'danger',
          toaster: 'b-toaster-top-right'
        });
      }).finally(() => {
        this.$refs.reanalyseModal.hide();
      });
    },
    getProductReanalyseStatus() {
      this.axios.get('admin/products/reanalysisStatus')
        .then((response) => {
          if (response.status === 200) {
            this.reanalysisInProgress = response.data.result.inProgress;
          }
        });
    },
    showDownloadModal(messageKey) {
      this.downloadModalMessageKey = messageKey;
      this.$refs.downloadModal.show();
    },
    hideDownloadModal() {
      this.$refs.downloadModal.hide();
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@colors';
.downloadBtn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #c95328;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.downloadBtnDoctor {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #3e67ec;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.assignBtnDoctor {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #3ee6ec;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.downloadBtnProcessProductReport {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: rgb(93, 129, 93);
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.moveProductToDoctorSectionBtn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: rgb(180, 154, 35);
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.invalid-product-count {
  color: crimson;
}
</style>
